import { api } from '@/api'
import {
  API_URL,
  FAVOURITE,
  getApiV1BaseUrl,
  GTM_EVENT,
  LOCAL_STORAGE_KEYS,
  ROUTES,
} from '@/constants'
import { ErrorCode } from '@/errors/errorCode'
import { logError } from '@/errors/log-error'
import { useStores } from '@/store/rootStoreContext'
import { ApiFavourite, ICurrentUser } from '@/utils/auth/auth'
import { logOut } from '@/utils/auth/log-out'
import { sendGTMEvent } from '@next/third-parties/google'
import { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime'
import { useRouter } from 'next/navigation'
import { useCallback } from 'react'
import useSWR, { Fetcher } from 'swr'

type LoginOptionsType = {
  redirect?: {
    disabled?: boolean
    reader?: string
    supplier?: string
    admin?: string
  }
}

export type LogInSuccess =
  | ICurrentUser
  | { requires2FASetup: true }
  | { requires2FAVerification: true }

const fetcher: Fetcher<ICurrentUser, string> = async (url: string) => {
  try {
    const res = await fetch(url, {
      credentials: 'include',
      method: 'GET',
    })

    if (!res.ok) return null

    return res.json()
  } catch (error) {
    return null
  }
}

export function useUser() {
  const store = useStores()
  const { data: user, mutate } = useSWR(
    `${getApiV1BaseUrl()}${API_URL.USERS.CURRENT_USER}`,
    fetcher,
    {
      onSuccess: (userData) => {
        try {
          store.userProfile.setProfile(userData) // @todo remove this once getting rid of mobx userProfile
        } catch (e) {}

        const providerSignUpUrl = localStorage.getItem(
          LOCAL_STORAGE_KEYS.GTM_EVENT.PROVIDER_SIGN_UP_URL
        )

        if (userData?.role === 'reader' && providerSignUpUrl) {
          sendGTMEvent({
            event: GTM_EVENT.SIGN_UP,
            'page path': providerSignUpUrl,
          })

          localStorage.removeItem(
            LOCAL_STORAGE_KEYS.GTM_EVENT.PROVIDER_SIGN_UP_URL
          )
        }
      },
      onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
        if (error.status === 401) return
        if (retryCount >= 1) return
        setTimeout(() => revalidate({ retryCount }), 10000)
      },
    }
  )
  const router = useRouter()

  const _logOut = useCallback(async () => {
    await logOut()
    await mutate()
    router.push(ROUTES.HOME)
  }, [router])

  const hasFavourite = useCallback(
    (id: number, type: FAVOURITE) => {
      return !!user?.favourites.some(
        (fav) => fav.object_id === id && fav.type === type
      )
    },
    [user]
  )

  const toggleFavourite = useCallback(
    async (id: number, type: FAVOURITE) => {
      if (!user?.id) return

      const [err, data] = await api.feTofe.favourites.toggle({
        object_id: id,
        type,
      })

      if (err) {
        // Leave the cached user untouched so the icon snaps back to the state
        // the server still holds.
        logError(ErrorCode.e00255, err, { objectId: id, type })
        return
      }

      const withoutItem = user.favourites.filter(
        (fav) => !(fav.object_id === id && fav.type === type)
      )

      mutate(
        {
          ...user,
          favourites: data.favourited
            ? [...withoutItem, { object_id: id, type } as ApiFavourite]
            : withoutItem,
        },
        { revalidate: false }
      )
    },
    [user, mutate]
  )

  const logIn = async (
    email: string,
    password: string,
    options: LoginOptionsType = {}
  ): Promise<[Error, null] | [null, LogInSuccess]> => {
    const formData = new FormData()
    formData.append('email', email.trim())
    formData.append('password', password)

    try {
      const res = await fetch(`${getApiV1BaseUrl()}${API_URL.AUTH.LOGIN}`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      })

      switch (res.status) {
        case 200: {
          const body = (await res.json().catch(() => null)) as {
            requires2FASetup?: boolean
            requires2FAVerification?: boolean
          } | null
          if (body?.requires2FASetup) {
            if (!options.redirect?.disabled) {
              router.push(ROUTES.AUTH.TWO_FA_SETUP)
            }
            return [null, { requires2FASetup: true }]
          }
          if (body?.requires2FAVerification) {
            if (!options.redirect?.disabled) {
              router.push(ROUTES.AUTH.TWO_FA_VERIFY)
            }
            return [null, { requires2FAVerification: true }]
          }

          const refreshedUser = await mutate()
          if (!options.redirect?.disabled) {
            redirectAdminAfterLogin(
              router,
              refreshedUser,
              options.redirect?.admin
            )
            redirectSupplierAfterLogin(
              router,
              refreshedUser,
              options.redirect?.supplier
            )
            redirectReaderAfterLogin(
              router,
              refreshedUser,
              options.redirect?.reader
            )
          }

          return [null, refreshedUser!]
        }
        case 400:
        case 401:
          return [new Error('Incorrect email or password'), null]
        case 403:
          return [
            new Error(
              'This account is no longer active. Please contact support if you believe this is an error.'
            ),
            null,
          ]
        default:
          return [new Error('Error logging in user'), null]
      }
    } catch (e: any) {
      return [e, null]
    }
  }

  return {
    user,
    isSupplier: user?.role === 'supplier',
    isReader: user?.role === 'reader',
    isAdmin: user?.role === 'admin',
    logOut: _logOut,
    logIn,
    hasFavourite,
    toggleFavourite,
    refreshUser: mutate,
    favourites: user?.favourites,
  }
}

function redirectSupplierAfterLogin(
  router: AppRouterInstance,
  user?: ICurrentUser,
  redirect?: string
) {
  if (!user || user.role !== 'supplier') {
    return
  }

  if (redirect) {
    router.push(redirect)

    return
  }

  /**
   * if user is supplier and has no subscription
   * redirect to supplier listing page
   */
  if (!user?.stripe_subscription_id) {
    const supplierListingId = user.suppliers[0]?.id
    router.push(
      `${ROUTES.SUPPLIER.LISTING}?supplier=${supplierListingId}&formName=listing-details`
    )

    return
  }

  router.push(ROUTES.SUPPLIER.SUMMARY)
}

function redirectAdminAfterLogin(
  router: AppRouterInstance,
  user?: ICurrentUser,
  redirect?: string
) {
  if (!user || user.role !== 'admin') {
    return
  }

  if (redirect) {
    router.push(redirect)
    return
  }

  router.push(ROUTES.ADMIN.ROOT)
}

function redirectReaderAfterLogin(
  router: AppRouterInstance,
  user?: ICurrentUser,
  redirect?: string
) {
  if (!user || user.role !== 'reader') {
    return
  }

  if (redirect) {
    router.push(redirect)

    return
  }

  router.push(ROUTES.READER.DASHBOARD)
}
